import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Select, Switch, Row, Col, message, Button, Space, Divider, Tag } from "antd";
import { useCreate, useUpdate } from "../../../../hooks";
import { contractManagementService } from "../../../../api/enhancedApi";
import { SERVICE_TYPE_OPTIONS } from "../../SupplierContract/constants";

const { Option } = Select;
const { TextArea } = Input;

const ProductDrawer = ({ visible, mode, initialValues, onSuccess, onCancel }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // 添加调试信息
    console.log("=== ProductDrawer 组件渲染 ===");
    console.log("Props - visible:", visible, "mode:", mode);
    console.log("Props - initialValues:", initialValues);

    // 创建和更新的hooks
    const createMutation = useCreate("products", contractManagementService.product.createProduct, {
        onSuccess: (response) => {
            setLoading(false);
            // 处理OperationResult格式
            if (response.data?.success) {
                message.success(response.data.message || "创建成功");
            } else {
                message.success("创建成功");
            }
            onSuccess();
        },
        onError: (error) => {
            setLoading(false);
            console.error("创建产品失败:", error);
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || "创建失败";
            message.error(errorMsg);
        },
    });

    const updateMutation = useUpdate("products", contractManagementService.product.updateProduct, {
        onSuccess: (response) => {
            setLoading(false);
            // 处理OperationResult格式
            if (response.data?.success) {
                message.success(response.data.message || "更新成功");
            } else {
                message.success("更新成功");
            }
            onSuccess();
        },
        onError: (error) => {
            setLoading(false);
            console.error("更新产品失败:", error);
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || "更新失败";
            message.error(errorMsg);
        },
    });

    // 当 visible 或 initialValues 变化时重新初始化表单
    useEffect(() => {
        console.log("=== ProductDrawer useEffect 触发 ===");
        console.log("visible:", visible);
        console.log("mode:", mode);
        console.log("initialValues:", initialValues);
        
        if (!visible) {
            // 抽屉关闭时重置表单
            form.resetFields();
            return;
        }

        // 抽屉打开时初始化表单
        if (mode === "edit" && initialValues) {
            console.log("=== 编辑模式，设置表单值 ===");
            console.log("设置的初始值:", initialValues);
            
            const formValues = {
                name: initialValues.name || "",
                code: initialValues.code || "",
                description: initialValues.description || "",
                service_type: initialValues.service_type || "CATERING",
                category: initialValues.category || "",
                unit: initialValues.unit || "PIECE",
                is_active: initialValues.is_active !== undefined ? initialValues.is_active : true,
                remarks: initialValues.remarks || "",
                tags: Array.isArray(initialValues.tags) ? initialValues.tags : [],
                default_applicable_airports: Array.isArray(initialValues.default_applicable_airports) 
                    ? initialValues.default_applicable_airports.join(",") : "",
                default_applicable_aircraft_types: Array.isArray(initialValues.default_applicable_aircraft_types) 
                    ? initialValues.default_applicable_aircraft_types.join(",") : "",
                default_applicable_routes: Array.isArray(initialValues.default_applicable_routes) 
                    ? initialValues.default_applicable_routes.join(",") : "",
            };
            
            console.log("=== 准备设置的表单值 ===", formValues);
            form.setFieldsValue(formValues);
            
            // 验证表单是否正确设置
            setTimeout(() => {
                const currentValues = form.getFieldsValue();
                console.log("=== 当前表单值 ===", currentValues);
            }, 100);
            
        } else if (mode === "add") {
            console.log("=== 新增模式，设置默认值 ===");
            form.resetFields();
            form.setFieldsValue({
                is_active: true,
                service_type: "CATERING",
                unit: "PIECE",
                tags: [],
                default_applicable_airports: "",
                default_applicable_aircraft_types: "",
                default_applicable_routes: "",
            });
        }
    }, [visible, mode, initialValues, form]);

    // 处理提交
    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            
            console.log("=== ProductDrawer handleSubmit ===");
            console.log("表单值:", values);
            console.log("模式:", mode);

            // 处理字符串字段转换为数组
            const processedValues = {
                ...values,
                default_applicable_airports: values.default_applicable_airports
                    ? values.default_applicable_airports.split(",").map(s => s.trim()).filter(s => s)
                    : [],
                default_applicable_aircraft_types: values.default_applicable_aircraft_types
                    ? values.default_applicable_aircraft_types.split(",").map(s => s.trim()).filter(s => s)
                    : [],
                default_applicable_routes: values.default_applicable_routes
                    ? values.default_applicable_routes.split(",").map(s => s.trim()).filter(s => s)
                    : [],
            };

            console.log("处理后的值:", processedValues);

            if (mode === "add") {
                createMutation.mutate(processedValues);
            } else {
                console.log("更新产品，ID:", initialValues.id);
                updateMutation.mutate({
                    id: initialValues.id,
                    data: processedValues
                });
            }
        } catch (error) {
            setLoading(false);
            console.error("表单验证失败:", error);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Drawer
            title={mode === "add" ? "新增产品" : "编辑产品"}
            width={800}
            open={visible}
            onClose={handleCancel}
            destroyOnHidden
            extra={
                <Space>
                    <Button onClick={handleCancel}>取消</Button>
                    <Button type="primary" loading={loading} onClick={handleSubmit}>
                        {mode === "add" ? "创建" : "保存"}
                    </Button>
                </Space>
            }
        >
            {/* 调试信息面板 - 开发时使用 */}
            {process.env.NODE_ENV === 'development' && (
                <div style={{ 
                    background: '#f5f5f5', 
                    padding: '8px', 
                    marginBottom: '16px', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'monospace'
                }}>
                    <div><strong>调试信息:</strong></div>
                    <div>visible: {visible.toString()}</div>
                    <div>mode: {mode}</div>
                    <div>initialValues: {initialValues ? JSON.stringify(initialValues, null, 2) : 'null'}</div>
                    <div>表单当前值: {JSON.stringify(form.getFieldsValue(), null, 2)}</div>
                </div>
            )}

            <Form 
                form={form} 
                layout="vertical" 
                scrollToFirstError
            >
                {/* 基本信息 */}
                <Divider orientation="left">基本信息</Divider>
                
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="name"
                            label="产品名称"
                            rules={[
                                { required: true, message: "请输入产品名称" },
                                { max: 200, message: "产品名称不能超过200个字符" },
                            ]}
                        >
                            <Input placeholder="请输入产品名称" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item 
                            name="code" 
                            label="产品编码"
                            rules={[{ max: 100, message: "产品编码不能超过100个字符" }]}
                        >
                            <Input placeholder="请输入产品编码" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item 
                            name="service_type" 
                            label="服务类型" 
                            rules={[{ required: true, message: "请选择服务类型" }]}
                        >
                            <Select placeholder="请选择服务类型" options={SERVICE_TYPE_OPTIONS} />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item
                            name="unit"
                            label="单位"
                            rules={[{ required: true, message: "请选择单位" }]}
                        >
                            <Select placeholder="请选择单位">
                                <Option value="PIECE">件</Option>
                                <Option value="PERSON">人</Option>
                                <Option value="FLIGHT">航班</Option>
                                <Option value="KG">公斤</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item 
                            name="category" 
                            label="产品类别"
                            rules={[{ max: 100, message: "产品类别不能超过100个字符" }]}
                        >
                            <Input placeholder="请输入产品类别" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="description" label="产品描述">
                    <TextArea rows={3} placeholder="请输入产品描述" />
                </Form.Item>

                {/* 适用规则 */}
                <Divider orientation="left">默认适用规则</Divider>
                
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item 
                            name="default_applicable_airports" 
                            label="默认适用机场" 
                        >
                            <Input 
                                placeholder="如: PEK,SHA,CAN" 
                                addonBefore={<Tag color="blue">机场</Tag>}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item 
                            name="default_applicable_aircraft_types" 
                            label="默认适用机型" 
                        >
                            <Input 
                                placeholder="如: A320,B737" 
                                addonBefore={<Tag color="green">机型</Tag>}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item 
                            name="default_applicable_routes" 
                            label="默认适用航线" 
                        >
                            <Input 
                                placeholder="如: PEK-SHA,SHA-CAN" 
                                addonBefore={<Tag color="orange">航线</Tag>}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* 其他设置 */}
                <Divider orientation="left">其他设置</Divider>
                
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item 
                            name="tags" 
                            label="产品标签"
                        >
                            <Select 
                                mode="tags" 
                                placeholder="请输入标签，回车添加" 
                                tokenSeparators={[","]}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item 
                            name="is_active" 
                            label="产品状态" 
                            valuePropName="checked"
                        >
                            <Switch 
                                checkedChildren="启用" 
                                unCheckedChildren="停用" 
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="remarks" label="备注信息">
                    <TextArea rows={2} placeholder="请输入备注信息" />
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default ProductDrawer; 